import { DollarSign } from 'lucide-react'
import * as React from 'react'
import { FormLabel } from './form-label'
import type { AnyFieldApi } from '@tanstack/react-form'
import { Input } from '@/components/ui/input'

export interface CurrencyInputProps extends React.ComponentProps<typeof Input> {
  field: AnyFieldApi
  label?: string
  placeholder?: string
  showIcon?: boolean
}

function parseCurrencyToCents(value: string): number | undefined {
  if (!value) return undefined
  // Keep digits, comma and dot and minus
  const cleaned = value.replace(/[^0-9.,-]/g, '')
  if (!cleaned) return undefined
  // Normalize comma to dot, but allow thousand separators (we'll strip spaces)
  const normalized = cleaned.replace(/,/g, '.')
  const num = parseFloat(normalized)
  if (Number.isNaN(num)) return undefined

  // If the input has no decimal point, treat it as dollars (not cents)
  if (!value.includes('.') && !value.includes(',')) {
    return Math.round(num * 100)
  }

  return Math.round(num * 100)
}

export function CurrencyInput({
  field,
  label,
  placeholder = '0.00',
  showIcon = true,
  ...props
}: CurrencyInputProps) {
  const fieldValue = field.state.value

  // Format a cents value into a localized number string without currency symbol
  const formatAmountNoSymbol = React.useCallback((cents: number) => {
    const num = cents / 100
    return new Intl.NumberFormat('en-CA', {
      style: 'decimal',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num)
  }, [])

  // fieldValue expected to be number of cents (number) or undefined/null
  const initialDisplay = React.useMemo(() => {
    if (!fieldValue) return ''
    if (typeof fieldValue === 'number') return formatAmountNoSymbol(fieldValue)
    // If value is string attempt to parse as number (cents) first
    const asNum = Number(fieldValue)
    if (!Number.isNaN(asNum)) {
      if (!asNum) return ''
      return formatAmountNoSymbol(asNum)
    }
    return String(fieldValue)
  }, [fieldValue])

  const [inputValue, setInputValue] = React.useState<string>(initialDisplay)
  const [isFocused, setIsFocused] = React.useState<boolean>(false)

  // merge any incoming className from props with padding for the left icon (only when shown)
  const inputClassName = [props.className, showIcon ? 'pl-9' : undefined]
    .filter(Boolean)
    .join(' ')

  React.useEffect(() => {
    // Only update display value if not focused
    if (!isFocused) {
      setInputValue(initialDisplay)
    }
  }, [initialDisplay, isFocused])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = e.target.value

    // Only allow digits, comma, dot, and minus sign
    const filteredValue = v.replace(/[^0-9.,-]/g, '')

    setInputValue(filteredValue)

    const cents = parseCurrencyToCents(filteredValue)
    if (cents !== undefined) {
      field.handleChange(cents)
    } else if (filteredValue.trim() === '') {
      field.handleChange(undefined)
    } else {
      // keep previous field value until valid number
    }
  }

  const handleFocus = () => {
    setIsFocused(true)
  }

  const handleBlur = () => {
    setIsFocused(false)

    // On blur, if input is empty, set to 0.00, otherwise format the value
    if (inputValue.trim() === '') {
      setInputValue('')
      field.handleChange(0)
    } else {
      // If we have a valid cents value, display formatted CAD
      const cents = parseCurrencyToCents(inputValue)
      if (cents !== undefined) {
        setInputValue(formatAmountNoSymbol(cents))
      }
    }

    // Always notify form blur
    field.handleBlur()
  }

  return (
    <div className="gap-0">
      {label && (
        <FormLabel htmlFor={props.name} required={props.required}>
          {label}
        </FormLabel>
      )}

      <div className="relative">
        {showIcon && (
          <DollarSign
            size={16}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"
          />
        )}
        <Input
          {...props}
          className={inputClassName}
          inputMode="decimal"
          pattern="[0-9.,\-]*"
          value={inputValue}
          placeholder={placeholder}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          type="text"
        />
      </div>

      {field.state.meta.isTouched && !field.state.meta.isValid ? (
        <p className="text-xs text-red-500">
          {field.state.meta.errors.join(', ')}
        </p>
      ) : null}
    </div>
  )
}

export default CurrencyInput
